import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { 
  Image as ImageIcon, 
  FileVideo, 
  Library, 
  Search,
  Play,
  Eye,
  Calendar,
  Gamepad2,
  MoreVertical,
  Download,
  ExternalLink,
  Upload,
  Loader2,
  Sparkles,
  Type,
  Tag,
  MessageSquare,
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  BrainCircuit,
  Trash2,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  CheckCircle2
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, isSameWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { usePostModal } from '../../contexts/PostModalContext';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { cn } from "../../lib/utils";
import { toast } from 'sonner';
import { getPostText } from '../../utils/general';

// shadcn UI
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { 
  Card, 
  CardContent 
} from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "../../components/ui/tabs";
import { Skeleton } from "../../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Progress } from "../../components/ui/progress";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../components/ui/alert";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";

import { API_BASE_URL } from '../../config';

import { useHistory, useParams } from 'react-router-dom';

type MediaContentType = 'TRAILER' | 'GAMEPLAY' | 'FEATURE' | 'SURVEY';

const CONTENT_TYPE_STYLES: Record<MediaContentType, string> = {
  TRAILER: 'bg-amber-500/90 text-white border-amber-600/50',
  GAMEPLAY: 'bg-green-500/90 text-white border-green-600/50',
  FEATURE: 'bg-blue-500/90 text-white border-blue-600/50',
  SURVEY: 'bg-purple-500/90 text-white border-purple-600/50',
};

interface PostMedia {
  id: string;
  filePath: string;
  thumbnailPath?: string;
  mediaType: string;
  createdAt: string;
  contentType?: MediaContentType | null;
  statusAiDiscovery?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  loadError?: boolean; // Client-side only state
  aiMetadata?: {
    videoTitle?: string;
    tags?: string[];
    isTruncated?: boolean;
    videoDuration?: number;
    description?: string;
    frameLogs?: Array<{ timestamp: number; events: string[]; scene?: string }>;
    category?: string;
    suggestedTexts?: {
      en: string[];
      es: string[];
    };
  };
  post?: {
    id: string;
    textEn?: string;
    textEs?: string;
    translations?: Record<string, { text: string, tiktokTitle?: string }>;
    game: {
      id: string;
      name: string;
    };
    schedules: Array<{
      id: string;
      socialAccountId: string;
      scheduledAt: string;
      status: string;
    }>;
  };
}

export default function MyMedia(): JSX.Element {
  const history = useHistory();
  const { mediaId: urlMediaId } = useParams<{ mediaId?: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState<PostMedia | null>(null);
  const [inspectedMedia, setInspectedMedia] = useState<PostMedia | null>(null);
  const [mediaToDelete, setMediaToDelete] = useState<PostMedia | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mediaDependencies, setMediaDependencies] = useState<any>(null);
  const [checkingDependencies, setCheckingDependencies] = useState(false);

  const [isReanalyzeDialogOpen, setIsReanalyzeDialogOpen] = useState(false);
  const [reanalyzeModel, setReanalyzeModel] = useState('gpt-4o-mini');
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  
  // Upload States
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'gameplay' | 'new feature' | 'trailer' | null>(null);
  const [uploadingMediaId, setUploadingMediaId] = useState<string | null>(null);
  
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const [mediaLoadErrors, setMediaLoadErrors] = useState<Record<string, boolean>>({});

  const { openPostModal, openPostById } = usePostModal();
  const { user } = useGlobalState();
  const token = localStorage.getItem('token');
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canStoreVideos = user?.featureFlags?.store_videos;

  useEffect(() => {
    if (canStoreVideos === false) {
      toast.info("Your plan uses Optimized External Storage. Media is managed through your Post History.");
      history.replace('/posts');
    }
  }, [canStoreVideos, history]);

  useEffect(() => {
    if (!inspectedMedia) {
      setIsTimelineExpanded(false);
    }
  }, [inspectedMedia]);

  // Fetch all media directly
  const { data: allMedia = [], isLoading } = useQuery<PostMedia[]>({
    queryKey: ['userMedia'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/social/user-media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  // Auto-open media from URL param /my-media/:mediaId
  useEffect(() => {
    if (!urlMediaId || isLoading || inspectedMedia) return;

    // First try to find it in the already-loaded list
    const found = allMedia.find(m => m.id === urlMediaId);
    if (found) {
      setInspectedMedia(found);
      return;
    }

    // Otherwise fetch individually
    const fetchSingle = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/social/media/${urlMediaId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInspectedMedia(response.data as PostMedia);
      } catch {
        toast.error('Media not found');
        history.replace('/my-media');
      }
    };
    fetchSingle();
  }, [urlMediaId, isLoading, allMedia]);

  const openMediaInspector = (media: PostMedia) => {
    setInspectedMedia(media);
    history.replace(`/my-media/${media.id}`);
  };

  const closeMediaInspector = () => {
    setInspectedMedia(null);
    history.replace('/my-media');
  };

  const updateMediaMetadata = async (mediaId: string, category: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/social/media/${mediaId}/metadata`, { category }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      queryClient.invalidateQueries({ queryKey: ['userMedia'] });
    } catch (err) {
      console.error('Failed to update media metadata:', err);
    }
  };

  const updateContentType = async (mediaId: string, newContentType: MediaContentType | null) => {
    try {
      await axios.patch(`${API_BASE_URL}/social/media/${mediaId}/metadata`, { contentType: newContentType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update inspected media state immediately
      if (inspectedMedia && inspectedMedia.id === mediaId) {
        setInspectedMedia({ ...inspectedMedia, contentType: newContentType });
      }
      queryClient.invalidateQueries({ queryKey: ['userMedia'] });
    } catch (err) {
      console.error('Failed to update content type:', err);
    }
  };

  const uploadMutation = useMutation({
    mutationFn: async ({ file, category }: { file: File, category?: string }) => {
      const formData = new FormData();
      formData.append('media', file);
      if (category) {
        formData.append('category', category);
      }
      setUploadProgress(0);
      const response = await axios.post(`${API_BASE_URL}/social/upload-media`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}` 
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      });
      return response.data;
    },
    onSuccess: (data) => {
      setUploadingMediaId(data.id);
      queryClient.invalidateQueries({ queryKey: ['userMedia'] });
      toast.success('Media uploaded successfully');
      // No longer closing automatically
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to upload media');
      setUploadProgress(null);
    }
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setIsUploadDialogOpen(true);
      setUploadingMediaId(null);
      setUploadProgress(0);
      
      // Start upload immediately
      uploadMutation.mutate({ file });
    }
  };

  const handleAcceptUpload = async () => {
    if (uploadingMediaId && selectedCategory) {
      await updateMediaMetadata(uploadingMediaId, selectedCategory);
    }
    
    setIsUploadDialogOpen(false);
    setUploadProgress(null);
    setUploadFile(null);
    setSelectedCategory(null);
    setUploadingMediaId(null);
  };

  const filteredMedia = useMemo(() => {
    return allMedia.filter(item => {
      const gameName = item.post?.game?.name || 'Standalone';
      
      const matchesSearch = gameName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          getPostText(item.post).toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = activeTab === 'all' || 
                         (activeTab === 'videos' && item.mediaType === 'video') ||
                         (activeTab === 'images' && item.mediaType === 'image');
      
      return matchesSearch && matchesType;
    });
  }, [allMedia, searchQuery, activeTab]);

  const groupedMedia = useMemo(() => {
    const groups: { [key: string]: { start: Date; end: Date; items: PostMedia[] } } = {};
    
    filteredMedia.forEach(item => {
      const date = new Date(item.createdAt);
      const start = startOfWeek(date, { weekStartsOn: 1 });
      const end = endOfWeek(date, { weekStartsOn: 1 });
      const key = format(start, 'yyyy-MM-dd');
      
      if (!groups[key]) {
        groups[key] = { start, end, items: [] };
      }
      groups[key].items.push(item);
    });
    
    return Object.keys(groups)
      .sort((a, b) => b.localeCompare(a))
      .map(key => groups[key]);
  }, [filteredMedia]);

  const handleOpenPost = (item: PostMedia) => {
    if (!item.post) {
      toast.info('This media is not assigned to any post yet.');
      return;
    }
    
    openPostById(item.post.id);
  };

  const checkDependencies = async (media: PostMedia) => {
    setMediaToDelete(media);
    setCheckingDependencies(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/social/media/${media.id}/dependencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMediaDependencies(res.data);
    } catch (err) {
      console.error('Failed to check dependencies:', err);
      toast.error('Failed to check media dependencies');
    } finally {
      setCheckingDependencies(false);
    }
  };

  const handleDeleteMedia = async () => {
    if (!mediaToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/social/media/${mediaToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Media deleted successfully');
      setMediaToDelete(null);
      setMediaDependencies(null);
      closeMediaInspector();
      queryClient.invalidateQueries({ queryKey: ['userMedia'] });
    } catch (err) {
      console.error('Failed to delete media:', err);
      toast.error('Failed to delete media');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReanalyze = async () => {
    if (!inspectedMedia) return;
    setIsReanalyzing(true);
    try {
      await axios.post(`${API_BASE_URL}/social/media/${inspectedMedia.id}/reanalyze`, {
        model: reanalyzeModel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Re-analysis triggered with ${reanalyzeModel}`);
      setIsReanalyzeDialogOpen(false);
      // Invalidate query to show pending status
      queryClient.invalidateQueries({ queryKey: ['userMedia'] });
      // Close inspector
      closeMediaInspector();
    } catch (err) {
      console.error('Failed to trigger re-analysis:', err);
      toast.error('Failed to trigger re-analysis');
    } finally {
      setIsReanalyzing(false);
    }
  };

  const getMediaUrl = (filePath: string) => {
    if (filePath === 'media/deleted.jpg') {
      return '/deleted-placeholder.jpg';
    }
    return `${API_BASE_URL}/social/media?path=${encodeURIComponent(filePath)}&token=${token}`;
  };

  const getThumbnailUrl = (filePath: string, w: number = 300) => {
    if (filePath === 'media/deleted.jpg') {
      return '/deleted-placeholder.jpg';
    }
    return `${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(filePath)}&w=${w}&token=${token}`;
  };

  return (
    <div className="container mx-auto py-10 px-4 max-w-7xl space-y-8 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20 shadow-lg shadow-primary/5">
            <Library className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight italic uppercase">Media Library</h1>
            <p className="text-muted-foreground font-medium">Manage and explore your media assets.</p>
          </div>
        </div>

        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative group flex-1 md:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
            <Input 
              placeholder="Search by game or content..." 
              className="pl-11 bg-card border-border rounded-2xl h-12 text-foreground focus:ring-2 focus:ring-primary/20 transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept="image/*,video/*"
            onChange={handleFileSelect}
          />
          
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="bg-primary hover:bg-primary/90 text-white font-bold h-12 px-6 rounded-2xl shadow-lg shadow-primary/20 gap-2"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Upload className="h-5 w-5" />
            )}
            Upload Media
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <TabsList className="bg-card border border-border p-1 h-12 rounded-2xl">
            <TabsTrigger value="all" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white">All Assets</TabsTrigger>
            <TabsTrigger value="videos" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white flex items-center gap-2">
              <FileVideo className="h-4 w-4" /> Videos
            </TabsTrigger>
            <TabsTrigger value="images" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white flex items-center gap-2">
              <ImageIcon className="h-4 w-4" /> Images
            </TabsTrigger>
          </TabsList>

          <Badge variant="outline" className="bg-muted/50 border-border text-muted-foreground py-2 px-4 rounded-full font-black uppercase tracking-widest text-[10px]">
            {filteredMedia.length} Items Found
          </Badge>
        </div>

        <TabsContent value={activeTab} className="mt-0">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Card key={i} className="bg-card border-border overflow-hidden rounded-3xl">
                  <Skeleton className="aspect-square w-full bg-card" />
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-4 w-1/2 bg-card" />
                    <Skeleton className="h-3 w-1/3 bg-card" />
                  </div>
                </Card>
              ))}
            </div>
          ) : groupedMedia.length > 0 ? (
            <div className="space-y-12">
              {groupedMedia.map((group, groupIdx) => (
                <div key={groupIdx} className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-card" />
                    <h2 className="text-sm font-black uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-primary" />
                      Week {format(group.start, 'MMM dd')} - {format(group.end, 'MMM dd, yyyy')}
                    </h2>
                    <div className="h-px flex-1 bg-card" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {group.items.map((item) => (
                      <Card 
                        key={item.id} 
                        onClick={() => item.mediaType === 'video' ? openMediaInspector(item) : null}
                        className={cn(
                          "group bg-card border-border hover:border-primary/50 overflow-hidden rounded-[2rem] transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10",
                          item.mediaType === 'video' && "cursor-pointer"
                        )}
                      >
                        <div className="aspect-square relative overflow-hidden">
                          {(item.filePath === 'media/deleted.jpg' || mediaLoadErrors[item.id]) ? (
                            <div className="w-full h-full bg-muted flex flex-col items-center justify-center space-y-2 border-2 border-dashed border-border">
                              <Trash2 className="h-8 w-8 text-muted-foreground" />
                              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                                {mediaLoadErrors[item.id] ? 'Load Error' : 'Media Deleted'}
                              </span>
                            </div>
                          ) : item.mediaType === 'video' ? (
                            <div className="w-full h-full bg-muted flex items-center justify-center">
                              {item.thumbnailPath ? (
                                <img
                                  src={getThumbnailUrl(item.thumbnailPath)}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                  alt="Thumbnail"
                                  onError={() => setMediaLoadErrors(prev => ({ ...prev, [item.id]: true }))}
                                />
                              ) : (
                                <video
                                  src={getMediaUrl(item.filePath)}
                                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                  onError={() => setMediaLoadErrors(prev => ({ ...prev, [item.id]: true }))}
                                />
                              )}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-all">
                                <div className="bg-primary p-4 rounded-full shadow-2xl scale-90 group-hover:scale-100 transition-transform">
                                  <Play className="h-6 w-6 text-white fill-current" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={getThumbnailUrl(item.filePath)}
                              alt={item.post?.game?.name || "Standalone"}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              onError={() => setMediaLoadErrors(prev => ({ ...prev, [item.id]: true }))}
                            />
                          )}
                          
                          {/* Overlay Actions */}
                          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                            <div className="flex flex-col gap-2 mb-2">
                              {item.mediaType === 'video' && (
                                <Button 
                                  size="sm" 
                                  className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl gap-2 font-bold shadow-lg shadow-primary/20"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMediaInspector(item);
                                  }}
                                >
                                  <Sparkles className="h-4 w-4" /> View AI Analysis
                                </Button>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {item.post ? (
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-white/10 hover:bg-white/20 backdrop-blur-md border-white/10 text-white rounded-xl gap-2 font-bold"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPost(item);
                                  }}
                                >
                                  <Eye className="h-4 w-4" /> View Post
                                </Button>
                              ) : (
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-white/10 hover:bg-white/20 backdrop-blur-md border-white/10 text-white rounded-xl gap-2 font-bold"
                                  disabled
                                >
                                  {item.aiMetadata?.videoTitle || 'Standalone Media'}
                                </Button>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    size="icon" 
                                    variant="ghost" 
                                    className="bg-white/10 hover:bg-white/20 backdrop-blur-md border-white/10 text-white rounded-xl"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-card border-border text-foreground">
                                  <DropdownMenuItem className="gap-2 focus:bg-primary focus:text-white cursor-pointer" onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(getMediaUrl(item.filePath), '_blank');
                                  }}>
                                    <Download className="h-4 w-4" /> Download Original
                                  </DropdownMenuItem>
                                  {item.post && (
                                    <DropdownMenuItem className="gap-2 focus:bg-primary focus:text-white cursor-pointer" onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenPost(item);
                                    }}>
                                      <ExternalLink className="h-4 w-4" /> Go to Project
                                    </DropdownMenuItem>
                                  )}
                                  <Separator className="bg-card my-1" />
                                  <DropdownMenuItem 
                                    className="gap-2 focus:bg-red-600 focus:text-white cursor-pointer text-red-400" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      checkDependencies(item);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" /> Delete Media
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          
                          <div className="absolute top-4 left-4 flex flex-col gap-2">
                            <Badge className={cn(
                              "rounded-lg px-2 py-1 font-black text-[10px] uppercase tracking-widest border-none shadow-lg w-fit",
                              item.mediaType === 'video' ? "bg-primary text-white" : "bg-amber-500 text-white"
                            )}>
                              {item.mediaType}
                            </Badge>
                            
                            {item.contentType && (
                              <Badge className={cn(
                                "rounded-lg px-2 py-1 font-black text-[8px] uppercase tracking-widest border shadow-lg w-fit",
                                CONTENT_TYPE_STYLES[item.contentType]
                              )}>
                                {item.contentType}
                              </Badge>
                            )}

                            {item.statusAiDiscovery === 'PROCESSING' && (
                              <Badge className="rounded-lg px-2 py-1 font-black text-[10px] uppercase tracking-widest bg-purple-600 text-white border-none shadow-lg flex items-center gap-1.5 w-fit animate-pulse">
                                <BrainCircuit className="h-3 w-3 animate-bounce" />
                                Learning...
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <CardContent className="p-5 space-y-3">
                          <div className="flex items-center gap-2 text-primary">
                            <Gamepad2 className="h-4 w-4" />
                            <span className="text-xs font-black uppercase tracking-widest truncate">{item.post?.game?.name || 'Standalone'}</span>
                          </div>
                          
                          <h3 className="text-sm font-bold text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                            {item.post?.textEn || item.post?.textEs || (item.aiMetadata as any)?.videoTitle || 'Standalone Media'}
                          </h3>

                          {item.aiMetadata?.tags && item.aiMetadata.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {item.aiMetadata.tags.map((tag: string, idx: number) => (
                                <span key={idx} className="text-[9px] font-bold text-muted-foreground bg-card/50 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                  <Tag className="h-2 w-2" /> {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(item.createdAt), 'MMM dd, yyyy')}
                            </div>
                            <div className="flex items-center gap-1">
                              {item.post?.schedules.length || 0} Platforms
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-[2.5rem] min-h-[400px] flex flex-col items-center justify-center text-center p-10 shadow-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-primary/[0.02] group-hover:bg-primary/[0.04] transition-all duration-700" />
              <div className="relative space-y-6">
                <div className="h-24 w-24 rounded-[2rem] bg-primary/10 border border-primary/20 flex items-center justify-center shadow-xl mx-auto">
                  <Library className="h-10 w-10 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-foreground">No Media Found</h3>
                  <p className="text-muted-foreground max-w-sm mx-auto font-medium leading-relaxed">
                    {searchQuery ? `No results for "${searchQuery}" in ${activeTab}` : "You haven't uploaded any media yet. Start by uploading something to your library!"}
                  </p>
                </div>
                {!searchQuery && (
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-xl bg-primary hover:bg-primary/90 text-white font-bold"
                  >
                    Upload your first asset
                  </Button>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Video Inspection Dialog */}
      <Dialog open={!!inspectedMedia} onOpenChange={(open) => {
        if (!open) closeMediaInspector();
      }}>
        <DialogContent className="max-w-6xl w-[95vw] h-[85vh] p-0 bg-background border-border overflow-hidden rounded-3xl flex flex-col sm:flex-row">
          {inspectedMedia && (
            <>
              {/* Left Panel: Video Player */}
              <div className="flex-1 bg-black flex items-center justify-center relative min-h-[300px] sm:min-h-0">
                {(inspectedMedia.filePath === 'media/deleted.jpg' || mediaLoadErrors[inspectedMedia.id]) ? (
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="p-8 bg-muted rounded-[3rem] border-2 border-dashed border-border">
                      <Trash2 className="h-20 w-20 text-muted-foreground" />
                    </div>
                    <div className="text-center space-y-1">
                      <h3 className="text-xl font-black text-muted-foreground uppercase tracking-tighter">
                        {mediaLoadErrors[inspectedMedia.id] ? 'Media Unavailable' : 'Media was Deleted'}
                      </h3>
                      <p className="text-xs text-muted-foreground font-medium">
                        {mediaLoadErrors[inspectedMedia.id] ? 'The file could not be loaded from the server.' : 'The source file has been removed from the server.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <video 
                    src={getMediaUrl(inspectedMedia.filePath)} 
                    controls 
                    className="max-h-full max-w-full"
                    autoPlay
                    onError={() => setMediaLoadErrors(prev => ({ ...prev, [inspectedMedia.id]: true }))}
                  />
                )}
                <div className="absolute top-6 left-6 flex flex-col gap-2">
                  <Badge className="bg-primary text-white border-none px-3 py-1 font-black uppercase tracking-widest text-xs">
                    {inspectedMedia.mediaType}
                  </Badge>
                  <Badge variant="outline" className="bg-black/50 backdrop-blur-md border-white/10 text-white font-mono text-[10px]">
                    {inspectedMedia.id.split('-')[0]}
                  </Badge>
                </div>
              </div>

              {/* Right Panel: Information */}
              <div className="w-full sm:w-[500px] bg-card border-l border-border flex flex-col overflow-hidden">
                <DialogHeader className="p-6 border-b border-border shrink-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-primary/10 p-2 rounded-xl border border-primary/20">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <DialogTitle className="text-xl font-black text-foreground tracking-tight italic">
                        {inspectedMedia.aiMetadata?.videoTitle || 'AI Analysis'}
                      </DialogTitle>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground font-medium">
                    {inspectedMedia.aiMetadata?.videoTitle ? 'Detailed AI analysis for this video.' : 'Extracted insights from your video content.'}
                  </p>
                </DialogHeader>

                <ScrollArea className="flex-1">
                  <div className="p-6 space-y-8 pb-10">
                    {/* Truncation Warning */}
                    {inspectedMedia.aiMetadata?.videoDuration && inspectedMedia.aiMetadata.videoDuration > 180 && (
                      <Alert className="bg-amber-500/10 border-amber-500/30 text-amber-400 rounded-2xl animate-in fade-in slide-in-from-top-2 duration-500">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle className="text-[10px] font-black uppercase tracking-widest">Long Video detected</AlertTitle>
                        <AlertDescription className="text-[11px] font-medium leading-relaxed">
                          This video is over 3 minutes long. Only the **first 2 minutes** were analyzed by AI.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Status & Category Section */}
                    <div className="space-y-6">
                      {inspectedMedia.statusAiDiscovery !== 'COMPLETED' && (
                      <div className="space-y-3">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Discovery Status</h4>
                        <div className="flex flex-wrap items-center gap-3">
                          <Badge className={cn(
                            "px-3 py-1 rounded-full font-black text-[10px] uppercase tracking-widest border-none",
                            inspectedMedia.statusAiDiscovery === 'PROCESSING' ? "bg-primary/10 text-primary animate-pulse" :
                            inspectedMedia.statusAiDiscovery === 'FAILED' ? "bg-red-500/10 text-red-400" :
                            "bg-slate-500/10 text-muted-foreground"
                          )}>
                            {inspectedMedia.statusAiDiscovery || 'PENDING'}
                          </Badge>
                        </div>
                      </div>
                      )}

                      {/* Content Type Selector */}
                      <div className="space-y-3">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Content Type</h4>
                        <div className="flex flex-wrap gap-2">
                          {(['TRAILER', 'GAMEPLAY', 'FEATURE', 'SURVEY'] as MediaContentType[]).map(type => (
                            <Button
                              key={type}
                              variant={inspectedMedia.contentType === type ? "default" : "outline"}
                              size="sm"
                              className={cn(
                                "text-[9px] uppercase tracking-wider font-black rounded-full h-7 px-3",
                                inspectedMedia.contentType === type
                                  ? CONTENT_TYPE_STYLES[type]
                                  : "border-border text-muted-foreground hover:border-primary/50"
                              )}
                              onClick={() => updateContentType(
                                inspectedMedia.id,
                                inspectedMedia.contentType === type ? null : type
                              )}
                            >
                              {type}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {inspectedMedia.aiMetadata?.tags && inspectedMedia.aiMetadata.tags.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Video Tags</h4>
                          <div className="flex flex-wrap gap-2">
                            {inspectedMedia.aiMetadata.tags.map((tag: string, idx: number) => (
                              <Badge key={idx} variant="secondary" className="bg-card text-muted-foreground border-border px-3 py-0.5 rounded-full font-bold text-[9px] uppercase tracking-wider">
                                #{tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {inspectedMedia.aiMetadata?.description && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Type className="h-4 w-4 text-primary" />
                          <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Scene Description</h4>
                        </div>
                        <p className="text-sm text-foreground leading-relaxed font-medium bg-muted/40 p-4 rounded-2xl border border-border/50">
                          {inspectedMedia.aiMetadata.description}
                        </p>
                      </div>
                    )}

                    {/* Suggested Texts */}
                    {inspectedMedia.aiMetadata?.suggestedTexts && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-primary" />
                          <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Post Suggestions</h4>
                        </div>
                        
                        <Tabs defaultValue="en_suggested" className="w-full">
                          <TabsList className="grid w-full grid-cols-2 bg-muted/50 border border-border h-9 p-1 rounded-xl">
                            <TabsTrigger value="en_suggested" className="text-[10px] font-black uppercase tracking-widest rounded-lg">English</TabsTrigger>
                            <TabsTrigger value="es_suggested" className="text-[10px] font-black uppercase tracking-widest rounded-lg">Spanish</TabsTrigger>
                          </TabsList>
                          <TabsContent value="en_suggested" className="mt-3 space-y-3">
                            {inspectedMedia.aiMetadata.suggestedTexts.en.map((text, idx) => (
                              <div key={idx} className="bg-primary/5 border border-primary/10 p-3 rounded-xl group relative hover:bg-primary/10 transition-colors">
                                <p className="text-xs text-foreground font-medium italic leading-relaxed">"{text}"</p>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => {
                                    navigator.clipboard.writeText(text);
                                    toast.success('Copied to clipboard');
                                  }}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </TabsContent>
                          <TabsContent value="es_suggested" className="mt-3 space-y-3">
                            {inspectedMedia.aiMetadata.suggestedTexts.es.map((text, idx) => (
                              <div key={idx} className="bg-amber-500/5 border border-amber-500/10 p-3 rounded-xl group relative hover:bg-amber-500/10 transition-colors">
                                <p className="text-xs text-foreground font-medium italic leading-relaxed">"{text}"</p>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => {
                                    navigator.clipboard.writeText(text);
                                    toast.success('Copied to clipboard');
                                  }}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </TabsContent>
                        </Tabs>
                      </div>
                    )}

                    {/* Timeline / Frame Logs */}
                    {inspectedMedia.aiMetadata?.frameLogs && inspectedMedia.aiMetadata.frameLogs.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-primary" />
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Video Timeline</h4>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
                            className="h-6 px-2 text-[9px] font-black uppercase tracking-widest text-primary hover:text-primary/80 hover:bg-primary/10 gap-1"
                          >
                            {isTimelineExpanded ? (
                              <>Show Less <ChevronUp className="h-3 w-3" /></>
                            ) : (
                              <>Show All <ChevronDown className="h-3 w-3" /></>
                            )}
                          </Button>
                        </div>
                        
                        <div className={cn(
                          "relative space-y-4 border-l-2 border-border ml-2 pl-4 transition-all duration-500 overflow-hidden",
                          !isTimelineExpanded ? "max-h-[120px]" : "max-h-[2000px]"
                        )}>
                          {inspectedMedia.aiMetadata.frameLogs.map((log, idx) => (
                            <div key={idx} className="relative">
                              <div className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.5)]" />
                              <div className="space-y-1">
                                <span className="text-[10px] font-black text-primary font-mono tracking-wider">
                                  {log.timestamp.toFixed(1)}s
                                </span>
                                <div className="flex flex-wrap gap-1">
                                  {log.scene && (
                                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[9px] font-black uppercase tracking-wider">
                                      {log.scene}
                                    </Badge>
                                  )}
                                  {log.events.map((event, eIdx) => (
                                    <Badge key={eIdx} variant="secondary" className="bg-card/50 text-muted-foreground border-border/50 text-[9px] font-bold">
                                      {event}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Gradient Fade to Black when collapsed */}
                          {!isTimelineExpanded && inspectedMedia.aiMetadata.frameLogs.length > 1 && (
                            <div 
                              className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#1e293b] via-[#1e293b]/80 to-transparent pointer-events-none" 
                              style={{ zIndex: 10 }}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Footer in Inspector */}
                    <div className="pt-6 border-t border-border space-y-3">
                      <Button 
                        onClick={() => {
                          if (inspectedMedia.post) {
                            handleOpenPost(inspectedMedia);
                            closeMediaInspector();
                          }
                        }}
                        disabled={!inspectedMedia.post}
                        className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-11 rounded-xl"
                      >
                        {inspectedMedia.post ? 'Go to Post Details' : 'Not assigned to a post'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => window.open(getMediaUrl(inspectedMedia.filePath), '_blank')}
                        disabled={inspectedMedia.filePath === 'media/deleted.jpg'}
                        className="w-full bg-transparent border-border hover:bg-card text-foreground font-bold h-11 rounded-xl gap-2"
                      >
                        <Download className="h-4 w-4" /> Download Video
                      </Button>

                      {user?.isAdmin && (
                        <Button 
                          variant="outline"
                          onClick={() => setIsReanalyzeDialogOpen(true)}
                          disabled={inspectedMedia.filePath === 'media/deleted.jpg' || inspectedMedia.statusAiDiscovery === 'PROCESSING'}
                          className="w-full bg-purple-600/10 hover:bg-purple-600 border border-purple-600/20 hover:border-purple-600 text-purple-400 hover:text-white font-bold h-11 rounded-xl gap-2 transition-all"
                        >
                          <RefreshCw className="h-4 w-4" /> Re-analyze AI
                        </Button>
                      )}
                      
                      <Button 
                        variant="destructive"
                        onClick={() => checkDependencies(inspectedMedia)}
                        disabled={inspectedMedia.filePath === 'media/deleted.jpg'}
                        className="w-full bg-red-600/10 hover:bg-red-600 border border-red-600/20 hover:border-red-600 text-red-500 hover:text-white font-bold h-11 rounded-xl gap-2 transition-all"
                      >
                        <Trash2 className="h-4 w-4" /> Delete Permanently
                      </Button>
                    </div>
                  </div>
                </ScrollArea>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!mediaToDelete} onOpenChange={(open) => !open && !isDeleting && setMediaToDelete(null)}>
        <DialogContent className="max-w-md bg-card border-border text-foreground rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-foreground flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to delete this media? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {checkingDependencies ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">Checking dependencies...</p>
            </div>
          ) : mediaDependencies?.pendingSchedules?.length > 0 ? (
            <div className="space-y-4 py-2">
              <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400 rounded-2xl">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="font-black uppercase tracking-widest text-[10px]">Warning: Scheduled Posts Detected</AlertTitle>
                <AlertDescription className="text-xs font-medium">
                  The following scheduled posts use this media and will be **permanently deleted**:
                </AlertDescription>
              </Alert>
              
              <ScrollArea className="h-32 border border-border rounded-xl bg-muted/50">
                <div className="p-3 space-y-2">
                  {mediaDependencies.pendingSchedules.map((s: any) => (
                    <div key={s.id} className="flex items-center justify-between text-[10px] bg-background/50 p-2 rounded-lg border border-border/50">
                      <div className="flex flex-col">
                        <span className="text-foreground font-bold">{s.socialAccount.name} ({s.socialAccount.platform})</span>
                        <span className="text-muted-foreground">{format(new Date(s.scheduledAt), 'MMM dd, HH:mm')}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 text-[9px] text-primary hover:text-primary/80 hover:bg-primary/10 gap-1 px-2"
                        onClick={() => {
                          setMediaToDelete(null);
                          openPostById(mediaDependencies.postId);
                        }}
                      >
                        View in Agenda <ChevronRight className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="py-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Deleting this file will free up space in your storage. 
                {mediaToDelete?.post && " It will be replaced by a placeholder in post history."}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setMediaToDelete(null)}
              disabled={isDeleting}
              className="border-border text-foreground hover:bg-card rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteMedia}
              disabled={isDeleting || checkingDependencies}
              className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl gap-2"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete Everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Re-analyze Dialog */}
      <Dialog open={isReanalyzeDialogOpen} onOpenChange={setIsReanalyzeDialogOpen}>
        <DialogContent className="max-w-sm bg-card border-border text-foreground rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-foreground flex items-center gap-2">
              <BrainCircuit className="h-5 w-5 text-purple-500" />
              AI Re-analysis
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Select an AI model to re-analyze this video. Useful for testing different outputs.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Vision Model</Label>
              <Select value={reanalyzeModel} onValueChange={setReanalyzeModel}>
                <SelectTrigger className="bg-muted border-border rounded-xl">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border text-foreground">
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast & Cheap)</SelectItem>
                  <SelectItem value="gpt-4o">GPT-4o (High Quality)</SelectItem>
                  <SelectItem value="o1-preview">o1 Preview (Experimental)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Alert className="bg-purple-500/10 border-purple-500/30 text-purple-400 rounded-2xl border">
              <Sparkles className="h-4 w-4" />
              <AlertTitle className="text-[10px] font-black uppercase tracking-widest">Admin Test Tool</AlertTitle>
              <AlertDescription className="text-xs font-medium">
                This will overwrite the current description, tags, and suggested texts.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsReanalyzeDialogOpen(false)}
              disabled={isReanalyzing}
              className="border-border text-foreground hover:bg-card rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReanalyze}
              disabled={isReanalyzing}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl gap-2"
            >
              {isReanalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Start Re-analysis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Progress Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={(open) => !open && !uploadMutation.isPending && setIsUploadDialogOpen(false)}>
        <DialogContent className="sm:max-w-[425px] bg-card border-border text-foreground p-0 overflow-hidden">
          <div className="p-8 space-y-6">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20">
                {uploadMutation.isPending ? (
                  <Loader2 className="h-6 w-6 text-primary animate-spin" />
                ) : uploadingMediaId ? (
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                ) : (
                  <Upload className="h-6 w-6 text-primary animate-bounce" />
                )}
              </div>
              <div>
                <DialogTitle className="text-xl font-black text-foreground tracking-tight italic uppercase">
                  {uploadMutation.isPending ? 'Uploading Asset' : uploadingMediaId ? 'Upload Complete' : 'Preparing Upload'}
                </DialogTitle>
                <p className="text-xs text-muted-foreground font-medium">
                  {uploadMutation.isPending ? 'Please wait while we process your file.' : uploadingMediaId ? 'Your media is ready. You can now classify it.' : 'Initializing transfer...'}
                </p>
              </div>
            </div>

            {(uploadProgress !== null || uploadingMediaId) && (
              <div className="space-y-3">
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  <span>Transfer Status</span>
                  <span className={cn(uploadingMediaId ? "text-green-400" : "text-primary")}>
                    {uploadingMediaId ? '100%' : `${uploadProgress}%`}
                  </span>
                </div>
                <Progress 
                  value={uploadingMediaId ? 100 : (uploadProgress || 0)} 
                  className={cn(
                    "h-3 bg-muted",
                    uploadingMediaId ? "[&>div]:bg-green-500" : "[&>div]:bg-primary"
                  )} 
                />
              </div>
            )}

            {uploadMutation.isPending && (
              <div className="bg-muted/50 rounded-xl p-4 border border-border/50">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  <span className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground">
                    {uploadProgress === 100 ? 'Encrypting & Verifying' : 'Transferring Data'}
                  </span>
                </div>
              </div>
            )}

            {/* Category Selector - Always visible for videos if upload started or finished */}
            {uploadFile?.type.startsWith('video/') && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-3 w-3 text-primary" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Classify this video</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'gameplay', label: 'Gameplay', icon: Gamepad2 },
                    { id: 'behind', label: 'New Feature', icon: Sparkles },
                    { id: 'trailer', label: 'Trailer', icon: FileVideo }
                  ].map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id as any)}
                      className={cn(
                        "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all gap-1.5",
                        selectedCategory === cat.id
                          ? "bg-primary/20 border-primary text-primary"
                          : "bg-muted/40 border-border text-muted-foreground hover:border-border hover:text-muted-foreground"
                      )}
                    >
                      <cat.icon className={cn("h-4 w-4", selectedCategory === cat.id ? "text-primary" : "text-muted-foreground")} />
                      <span className="text-[9px] font-bold uppercase tracking-tight">{cat.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[8px] text-muted-foreground italic text-center">
                  This helps our AI better understand your content for future missions.
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              {!uploadingMediaId && uploadMutation.isPending && (
                <p className="text-[10px] text-muted-foreground italic flex-1 self-center">Upload in progress...</p>
              )}
              
              {uploadingMediaId && (
                <Button 
                  onClick={handleAcceptUpload}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl px-10 shadow-lg shadow-green-500/20 animate-in zoom-in-95 duration-300"
                >
                  ACCEPT
                </Button>
              )}
              
              {!uploadingMediaId && !uploadMutation.isPending && (
                 <Button 
                  variant="outline" 
                  onClick={() => setIsUploadDialogOpen(false)}
                  className="rounded-xl border-border text-muted-foreground"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
